import { useCallback, useEffect, useRef } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import type { AxiosError, AxiosInstance } from 'axios';

import {
  jwtReactQueryAxiosInstance,
  oktaReactQueryAxiosInstance,
} from '@/react-query/api';
import routesConstants from '@/routes/routes.constants';
import { sendException } from '@/utils/sentry';

import { useHandleLogout } from '../../app/appHeader/utils';
import { endpoints } from '../../react-query/endpoints';
import {
  getExternalJwtToken,
  jwtAxiosInstace,
  oktaAxiosInstance,
} from '../../utils/api';

function normalizeUrl(url?: string): string {
  if (!url) return '';
  const parsed = new URL(url);

  // Normalize the path — replace IDs or hashes with placeholders
  const normalizedPath = parsed.pathname
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      // Mongo ObjectId or UUID-like patterns
      if (/^[0-9a-f]{24}$/i.test(segment)) return ':id';
      if (
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          segment,
        )
      )
        return ':id';
      if (/^\d+$/.test(segment)) return ':id'; // numeric IDs
      return segment;
    })
    .join('/');

  return `/${normalizedPath}`;
}

// Helper function to get token with retries
const getTokenWithRetry = async (
  oktaAuth: any,
  maxRetries = 3,
  baseDelay = 100,
): Promise<string | undefined> => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const token = await oktaAuth.getOrRenewAccessToken();
    if (token) {
      return token;
    }

    // If this isn't the last attempt, wait with exponential backoff
    if (attempt < maxRetries - 1) {
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  return undefined;
};

const WithAxios = ({ children }: { children: JSX.Element }) => {
  const { logout } = useHandleLogout();
  const { oktaAuth } = useOktaAuth();

  // Keep track of interceptor IDs so we can remove them when re-setting up
  const interceptorRefs = useRef<{
    oktaAxios: { request: number | null; response: number | null };
    oktaReactQuery: { request: number | null; response: number | null };
    jwtAxios: { request: number | null; response: number | null };
    jwtReactQuery: { request: number | null; response: number | null };
  }>({
    oktaAxios: { request: null, response: null },
    oktaReactQuery: { request: null, response: null },
    jwtAxios: { request: null, response: null },
    jwtReactQuery: { request: null, response: null },
  });

  const addPublicInterceptors = useCallback((instance: AxiosInstance) => {
    const interceptor = interceptorRefs.current.jwtAxios;

    // Remove existing interceptors if they exist
    if (interceptor.request !== null) {
      instance.interceptors.request.eject(interceptor.request);
    }
    if (interceptor.response !== null) {
      instance.interceptors.response.eject(interceptor.response);
    }

    // Public routes only use JWT token from localStorage
    const requestInterceptorId = instance.interceptors.request.use(
      async (config) => {
        const externalJwtToken = getExternalJwtToken();
        if (externalJwtToken && externalJwtToken.trim() !== '') {
          config.headers.Authorization = `Bearer ${externalJwtToken}`;
        }

        // all patient-downloadable routes should be prefixed with /external/patient-downloadable
        if (
          config.url &&
          !config.url.includes(
            `${endpoints.external}/${endpoints.patientDownloadable}`,
          )
        ) {
          const url = new URL(config.url);
          const pathSegments = url.pathname.split('/').filter(Boolean);

          // Check if the first segment is an API version (like v2, v3, etc.)
          const hasApiVersion =
            pathSegments.length > 0 && /^v\d+$/.test(pathSegments[0]);

          if (hasApiVersion) {
            // Insert /external/patient-downloadable after the API version
            const apiVersion = pathSegments[0];
            const remainingPath = pathSegments.slice(1).join('/');
            url.pathname = `/${apiVersion}/${endpoints.external}/${endpoints.patientDownloadable}/${remainingPath}`;
          } else {
            // No API version, prepend as before
            url.pathname = `/${endpoints.external}/${endpoints.patientDownloadable}${url.pathname}`;
          }

          config.url = url.toString();
        }

        return config;
      },
    );

    const responseInterceptorId = instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        throw error;
      },
    );

    // Store the interceptor IDs for future cleanup
    interceptor.request = requestInterceptorId;
    interceptor.response = responseInterceptorId;
  }, []);

  // need a different interceptor for react query because it uses relative paths (for eg /fmx-summaries instead of http://localhost:8080/fmx-summaries)
  const addPublicInterceptorsForReactQuery = useCallback(
    (instance: AxiosInstance) => {
      const interceptor = interceptorRefs.current.jwtReactQuery;

      // Remove existing interceptors if they exist
      if (interceptor.request !== null) {
        instance.interceptors.request.eject(interceptor.request);
      }
      if (interceptor.response !== null) {
        instance.interceptors.response.eject(interceptor.response);
      }

      // Public routes only use JWT token from localStorage
      const requestInterceptorId = instance.interceptors.request.use(
        async (config) => {
          const externalJwtToken = getExternalJwtToken();
          if (externalJwtToken && externalJwtToken.trim() !== '') {
            config.headers.Authorization = `Bearer ${externalJwtToken}`;
          }

          // Handle relative paths for React Query instances
          // all patient-downloadable routes should be prefixed with /external/patient-downloadable
          if (
            config.url &&
            !config.url.includes(
              `${endpoints.external}/${endpoints.patientDownloadable}`,
            )
          ) {
            // Ensure the path starts with / and add the prefix
            const path = config.url.startsWith('/')
              ? config.url
              : `/${config.url}`;

            const pathSegments = path.split('/').filter(Boolean);

            // Check if the first segment is an API version (like v2, v3, etc.)
            const hasApiVersion =
              pathSegments.length > 0 && /^v\d+$/.test(pathSegments[0]);

            if (hasApiVersion) {
              // Insert /external/patient-downloadable after the API version
              const apiVersion = pathSegments[0];
              const remainingPath = pathSegments.slice(1).join('/');
              config.url = `/${apiVersion}/${endpoints.external}/${endpoints.patientDownloadable}/${remainingPath}`;
            } else {
              // No API version, prepend as before
              config.url = `/${endpoints.external}/${endpoints.patientDownloadable}${path}`;
            }
          }

          return config;
        },
      );

      const responseInterceptorId = instance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
          throw error;
        },
      );

      // Store the interceptor IDs for future cleanup
      interceptor.request = requestInterceptorId;
      interceptor.response = responseInterceptorId;
    },
    [],
  );

  const addAuthenticatedInterceptors = useCallback(
    (instance: AxiosInstance, isReactQuery = false) => {
      // Define the current interceptor to avoid repeated conditions
      const interceptor =
        interceptorRefs.current[isReactQuery ? 'oktaReactQuery' : 'oktaAxios'];

      // Remove existing interceptors if they exist
      if (interceptor.request !== null) {
        instance.interceptors.request.eject(interceptor.request);
      }
      if (interceptor.response !== null) {
        instance.interceptors.response.eject(interceptor.response);
      }

      // Authenticated routes use Okta tokens
      const requestInterceptorId = instance.interceptors.request.use(
        async (config) => {
          // Try to get the fresh token with retries
          const freshToken = await getTokenWithRetry(oktaAuth);

          if (!freshToken) {
            // Check if this is the /me endpoint used for magic link auth
            // Magic link auth requires the first request to /me to proceed without a token
            const isMeEndpoint =
              config.url?.includes('/api/v1/sessions/me') || false;

            // Only log to Sentry if this is NOT the /me endpoint
            if (!isMeEndpoint) {
              const error = new Error(
                'Failed to retrieve access token after 3 attempts. Please try logging in again.',
              );
              sendException(error, {
                tags: [['action', 'AUTH_TOKEN_RETRY_FAILED']],
                extras: {
                  requestUrl: config.url,
                  requestMethod: config.method,
                  baseURL: config.baseURL,
                },
              });
            }

            // Allow the request to proceed without a token
            // This is necessary for magic link auth where the /me endpoint
            // is called before a token is available
            // there might be other cases too, so don't wanna break flow for now by throwing an error
          }

          if (freshToken) {
            config.headers.Authorization = `Bearer ${freshToken}`;
          }

          return config;
        },
      );

      const responseInterceptorId = instance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
          const oktaTokenStorage = localStorage.getItem('okta-token-storage');

          const oktaTokenStorageParsed = oktaTokenStorage
            ? JSON.parse(oktaTokenStorage)
            : null;

          const accessToken = await oktaAuth.tokenManager
            .getTokens()
            .then((token) => token?.accessToken);

          const tokenExpiry = accessToken
            ? new Date(accessToken.expiresAt * 1000).toISOString()
            : null;

          const isTokenExpired =
            !!tokenExpiry && new Date(tokenExpiry) < new Date();

          switch (error?.response?.status) {
            case 403: {
              if (error.request.responseURL.includes(endpoints.events)) {
                break;
              }

              // Check if we've already retried this request
              const hasRetried = (error.config as any)?._retry;

              if (!hasRetried && error.config) {
                // First 403: try to refresh token and retry the request
                // getOrRenewAccessToken() automatically handles refresh token rotation
                // per Okta best practices: https://developer.okta.com/docs/guides/refresh-tokens/main/
                try {
                  // Check if we have tokens before attempting refresh
                  // This prevents AuthSdkError: "The tokenManager has no token for the key: accessToken"
                  const tokens = await oktaAuth.tokenManager.getTokens();
                  const hasRefreshToken = !!tokens?.refreshToken;

                  // Only attempt refresh if we have a refresh token available
                  if (!hasRefreshToken) {
                    // No refresh token available, cannot refresh - fall through to logout
                    console.warn(
                      'No refresh token available for token refresh',
                    );
                  } else {
                    // Mark this request as retried to avoid infinite loops
                    (error.config as any)._retry = true;

                    // Refresh the token using Okta's recommended method
                    // This will use refresh tokens if available and handle rotation automatically
                    const refreshedToken =
                      await oktaAuth.getOrRenewAccessToken();

                    if (refreshedToken) {
                      // Update the authorization header with the new token
                      error.config.headers = error.config.headers || {};
                      error.config.headers.Authorization = `Bearer ${refreshedToken}`;

                      // Retry the original request with the refreshed token
                      return instance.request(error.config);
                    }
                  }
                } catch (refreshError: any) {
                  // If token refresh fails (e.g., refresh token expired/invalid, or no tokens available)
                  // This is expected behavior when tokens are missing/expired, so we don't log to Sentry
                  const isAuthSdkError =
                    refreshError?.name === 'AuthSdkError' &&
                    refreshError?.errorSummary?.includes(
                      'tokenManager has no token',
                    );

                  if (!isAuthSdkError) {
                    // Only log non-expected errors
                    console.error('Token refresh failed:', refreshError);
                  } else {
                    console.warn(
                      'Token refresh skipped - no tokens available in tokenManager',
                    );
                  }
                }
              }

              // Second 403 (after retry) or refresh failed: logout
              const responseData = error.response?.data as
                | { code?: string; message?: string }
                | undefined;

              await logout(undefined, {
                logoutType: 'FORCED',
                responseUrl: error.request?.responseURL,
                responseUrlPathNormalized: normalizeUrl(
                  error.request?.responseURL,
                ),
                httpStatus: error.response?.status,
                httpMethod: error.config?.method,
                requestHeaders: error.config?.headers,
                responseHeaders: error.response?.headers,
                responseData,
                errorCode: responseData?.code,
                errorMessage: responseData?.message,
                currentUrl: window.location.href,
                page: Object.entries(routesConstants).find(([, path]) =>
                  location.hash.includes(path),
                )?.[0],
                oktaSessionExists: await oktaAuth.session.exists(),
                oktaAccessToken: accessToken?.accessToken,
                oktaTokenStorage:
                  oktaTokenStorageParsed?.accessToken?.accessToken,
                isTokenExpired,
                tokenExpiry,
                isAuthenticated: await oktaAuth.isAuthenticated(),
                hasRetried,
              });
              break;
            }
            default:
              break;
          }
          // Sentry logging disabled for axios errors
          // const statusCode = error?.response?.status;
          // if (statusCode !== 403) {
          //   sendException(error, { tags: [['action', 'AXIOS_ERROR']] });
          // }
          throw error;
        },
      );

      // Store the interceptor IDs for future cleanup
      interceptor.request = requestInterceptorId;
      interceptor.response = responseInterceptorId;
    },
    [logout, oktaAuth],
  );

  addPublicInterceptors(jwtAxiosInstace);
  addPublicInterceptorsForReactQuery(jwtReactQueryAxiosInstance);

  addAuthenticatedInterceptors(oktaAxiosInstance, false);
  addAuthenticatedInterceptors(oktaReactQueryAxiosInstance, true);

  // Listen for token renewal events and re-setup authenticated interceptors
  useEffect(() => {
    if (!oktaAuth?.tokenManager) return;

    const handleTokenRenewed = async () => {
      // Re-setup authenticated interceptors when tokens are renewed to ensure fresh token usage
      addAuthenticatedInterceptors(oktaAxiosInstance, false);
      addAuthenticatedInterceptors(oktaReactQueryAxiosInstance, true);
    };

    const handleTokenExpired = async () => {
      // Re-setup authenticated interceptors when tokens expire to handle the next renewal
      addAuthenticatedInterceptors(oktaAxiosInstance, false);
      addAuthenticatedInterceptors(oktaReactQueryAxiosInstance, true);
    };

    // Listen for token renewal events
    oktaAuth.tokenManager.on('renewed', handleTokenRenewed);
    oktaAuth.tokenManager.on('expired', handleTokenExpired);

    // Cleanup event listeners
    return () => {
      oktaAuth.tokenManager.off('renewed', handleTokenRenewed);
      oktaAuth.tokenManager.off('expired', handleTokenExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oktaAuth?.tokenManager, addAuthenticatedInterceptors]);

  return children;
};

export default WithAxios;
